import {
    CardSchemeMarks,
    PayLaterMark,
    PaypalWordmark
} from './PaymentMethodMarks'

export const PaymentMethod = {
    CARD: 'card',
    PAYPAL: 'paypal',
    VENMO: 'venmo',
    PAY_LATER: 'paylater',
    GOOGLE_PAY: 'googlepay',
    APPLE_PAY: 'applepay'
}

// Each entry pairs one of our method ids with the funding sources PayPal's eligibility API knows
// it by, most capable first. Card has two: "advanced_cards" is the embedded card fields, and
// "card" is PayPal's own guest checkout form — the fallback when this merchant isn't enabled
// for advanced cards. Whichever is eligible ends up on the built method as `fundingSource`.
const METHOD_DEFINITIONS = [
    {id: PaymentMethod.CARD, fundingSources: ['advanced_cards', 'card'], label: 'Credit Card', mark: <CardSchemeMarks/>},
    {id: PaymentMethod.PAYPAL, fundingSources: ['paypal'], label: 'PayPal', mark: <PaypalWordmark/>},
    {id: PaymentMethod.PAY_LATER, fundingSources: ['paylater'], label: 'PayPal Pay Later', mark: <PayLaterMark/>}
]

// The funding source behind the card row when the merchant has advanced (embedded) card fields.
// Anything else means the row falls back to PayPal's hosted guest checkout form.
export const ADVANCED_CARDS = 'advanced_cards'

/**
 * Apple Pay is only offered on a device that can actually present the sheet. PayPal can report
 * the merchant as eligible on, say, Chrome on Android — where the button would never work.
 */
function canPresentApplePay() {
    try {
        return Boolean(window.ApplePaySession && window.ApplePaySession.canMakePayments())
    } catch (error) {
        return false
    }
}

/**
 * Turns PayPal's eligibility response into the list of methods to offer, in the order the
 * design lists them. A method the buyer, merchant or currency isn't eligible for never appears.
 */
export function buildEligibleMethods(eligiblePaymentMethods) {
    if (!eligiblePaymentMethods) return []

    return METHOD_DEFINITIONS
        .map(({fundingSources, ...definition}) => ({
            ...definition,
            fundingSource: fundingSources.find((source) => eligiblePaymentMethods.isEligible(source))
        }))
        .filter((method) => {
            if (!method.fundingSource) return false
            if (method.id === PaymentMethod.APPLE_PAY) return canPresentApplePay()
            return true
        })
}

/**
 * Splits an amount into PayPal's Pay in 4 schedule: three equal payments with the last one
 * absorbing the rounding remainder.
 */
export function splitIntoInstallments(totalAmount) {
    const totalMinorUnits = Math.round(Number(totalAmount) * 100)
    if (!Number.isFinite(totalMinorUnits) || totalMinorUnits <= 0) return []

    const each = Math.round(totalMinorUnits / 4)
    const last = totalMinorUnits - each * 3

    return [each, each, each, last].map((minorUnits) => minorUnits / 100)
}
